import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ExpressIncomingCallModal from "./ExpressIncomingCallModal";
import { useAuth } from "../contexts/AuthContext";
import { useWebSocketNotifications } from "../hooks/useWebSocket";

/**
 * Global mount point for the incoming Express call modal.
 * Listens to WS "new_booking" events with booking_type=express and only
 * displays for drivers. Vibrates the device on open when supported.
 *
 * Also owns its OWN WebSocket connection so the driver keeps receiving
 * incoming Express calls even when they're not on the /driver page
 * (e.g. on /driver/express, /admin/express-live, etc.).
 */
export default function ExpressCallListener() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  // Keep a persistent WebSocket connection for drivers regardless of route.
  // Passing null-ish userId disables the hook internally.
  useWebSocketNotifications(user?.role === "driver" ? user?.id : null);

  useEffect(() => {
    if (user?.role !== "driver") return;
    const onMessage = (event) => {
      const data = event.detail;
      if (!data) return;
      if (data.type === "express_request_expired") {
        // Backend told us the offer already moved to another driver.
        setOffer((cur) => (cur && cur.booking_id === data.booking_id ? null : cur));
        return;
      }
      if (data.type !== "new_booking" || data.booking_type !== "express") return;
      setOffer(data);
      // Strong vibration pattern
      if (navigator.vibrate) navigator.vibrate([400, 150, 400, 150, 400, 150, 400]);
    };
    window.addEventListener("trigo:ws-message", onMessage);
    return () => window.removeEventListener("trigo:ws-message", onMessage);
  }, [user?.role]);

  // Global auto-redirect on Express acceptance. Any accept path
  // (incoming call modal, pending queue, course card) dispatches
  // `trigo:express-accepted`; here we make sure the driver lands on
  // the Courses Express tab where they can manage the confirmed ride.
  useEffect(() => {
    if (user?.role !== "driver") return;
    const onAccepted = () => {
      try {
        if (window.location.pathname !== "/driver") {
          navigate("/driver?tab=express-bookings");
        } else {
          window.dispatchEvent(new CustomEvent("trigo:switch-tab", { detail: { tab: "express-bookings" } }));
        }
      } catch { /* noop */ }
    };
    window.addEventListener("trigo:express-accepted", onAccepted);
    return () => window.removeEventListener("trigo:express-accepted", onAccepted);
  }, [user?.role, navigate]);

  // Auto-reactivate persistent Express connection on ANY page mount so a driver
  // who navigates to /driver (Location) or refreshes the browser stays online.
  useEffect(() => {
    if (user?.role !== "driver") return;
    let flag = "0";
    try { flag = localStorage.getItem("trigo:express-persistent") || "0"; } catch { /* noop */ }
    if (flag !== "1") return;
    // Fire silently — server will no-op if already online.
    (async () => {
      try {
        await import("../lib/api").then(({ default: api }) =>
          api.post("/driver/express/toggle-online", { online: true }).catch(() => {})
        );
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              try {
                const { default: api } = await import("../lib/api");
                api.post("/location/update", { lat: pos.coords.latitude, lng: pos.coords.longitude, booking_id: "" }).catch(() => {});
              } catch { /* noop */ }
            },
            () => {},
            { enableHighAccuracy: true, timeout: 6000 }
          );
        }
      } catch { /* noop */ }
    })();

    // Periodic location heartbeat every 4 min so the driver stays fresh on
    // the admin map (10-min freshness window on backend).
    const iv = setInterval(() => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { default: api } = await import("../lib/api");
            api.post("/location/update", { lat: pos.coords.latitude, lng: pos.coords.longitude, booking_id: "" }).catch(() => {});
          } catch { /* noop */ }
        },
        () => {},
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 }
      );
    }, 4 * 60 * 1000);
    return () => clearInterval(iv);
  }, [user?.role, user?.id]);

  const handleAccepted = (o) => {
    setOffer(null);
    // Redirect to the driver dashboard's Courses Express tab so the driver can
    // manage the confirmed ride immediately, regardless of where they were.
    try {
      if (window.location.pathname !== "/driver") {
        navigate("/driver?tab=express-bookings");
      } else {
        window.dispatchEvent(new CustomEvent("trigo:switch-tab", { detail: { tab: "express-bookings" } }));
      }
    } catch { /* noop */ }
  };

  return (
    <ExpressIncomingCallModal
      open={!!offer}
      offer={offer}
      onAccepted={handleAccepted}
      onDeclined={() => setOffer(null)}
      onClose={() => setOffer(null)}
    />
  );
}
